<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import SoundCalling from 'dashboard/assets/audio/calling.mp3';
import SoundRinging from 'dashboard/assets/audio/ring.mp3';
import Avatar from 'next/avatar/Avatar.vue';
import Draggable from './Draggable.vue';
import Microphone from './icons/Microphone.vue';
import MicrophoneSlash from './icons/MicrophoneSlash.vue';
import VideoIcon from './icons/Video.vue';
import PhoneSlash from './icons/PhoneSlash.vue';
import NumpadVue from './icons/Numpad.vue';
import PhoneTransfer from './icons/PhoneTransfer.vue';
import Phone from './icons/Phone.vue';
import parsePhoneNumber from 'libphonenumber-js';
import voiceCallsAPI from 'dashboard/api/voiceCalls';

const store = useStore();

// Maps `${token}:${phone}` → { wavoip_call_id, message_id, direction }
const activeCalls = new Map();

function callKey(token, phone) {
  return `${token}:${String(phone || '').replace(/\D/g, '')}`;
}

function extractCallId(signalingData) {
  const c = signalingData?.content || {};
  return (
    c.call_id ||
    c.callId ||
    c.id ||
    signalingData?.call_id ||
    signalingData?.id ||
    null
  );
}

async function logCall({
  token,
  phone,
  direction,
  status,
  wavoip_call_id,
  from_tag,
  profile_picture,
  duration,
}) {
  if (!token || !phone || !wavoip_call_id) return null;
  try {
    const { data } = await voiceCallsAPI.log({
      token,
      phone: String(phone).replace(/\D/g, ''),
      direction,
      status,
      wavoip_call_id,
      from_tag,
      profile_picture,
      duration,
    });
    return data;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[webphone] failed to log call', e?.message);
    return null;
  }
}

async function fetchRecordingForMessage(messageId) {
  if (!messageId) return;
  try {
    await voiceCallsAPI.fetchRecording(messageId);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[webphone] failed to request recording', e?.message);
  }
}

const elapsedTime = ref(0);
const timer = ref(null);
const isMuted = ref(false);
const callingSound = ref(null);
const ringSound = ref(null);
const localStream = ref(null);
const resetCallTimeout = ref(null);

const uiFlags = computed(() => store.getters['webphone/getUIFlags']);
const callInfo = computed(() => store.getters['webphone/getCallInfo']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const wavoip = computed(() => store.getters['webphone/getWavoip']);

const username = computed(
  () => callInfo.value.tag ?? callInfo.value.phone ?? ''
);
const isCallEndDisabled = computed(() =>
  ['terminate', 'reject', 'accept_elsewhere', 'reject_elsewhere'].includes(
    callInfo.value.status
  )
);
const formattedDuration = computed(() => {
  const minutes = Math.floor(elapsedTime.value / 60);
  const seconds = elapsedTime.value % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

function startWavoip(inboxName, token) {
  store.dispatch('webphone/startWavoip', { token, inboxName });
}

function startTimer() {
  if (timer.value) clearInterval(timer.value);
  const startDate = store.state.webphone.call.active_start_date;
  if (!startDate) return;
  timer.value = setInterval(() => {
    const now = new Date();
    elapsedTime.value = Math.floor((now - startDate) / 1000);
  }, 1000);
}

function stopTimer() {
  clearInterval(timer.value);
  timer.value = null;
  elapsedTime.value = 0;
}

function closeWebphone() {
  store.dispatch('webphone/updateWebphoneVisible', { isOpen: false });
}

function acceptCall() {
  store.dispatch('webphone/acceptCall');
}

function rejectCall() {
  store.dispatch('webphone/rejectCall');
}

function endCall() {
  store.dispatch('webphone/endCall');
}

function mute() {
  isMuted.value = true;
  if (localStream.value && localStream.value.getAudioTracks().length > 0) {
    localStream.value.getAudioTracks()[0].enabled = false;
  }
}

function unMute() {
  isMuted.value = false;
  if (localStream.value && localStream.value.getAudioTracks().length > 0) {
    localStream.value.getAudioTracks()[0].enabled = true;
  }
}

function listenSockets(token, whatsapp_instance) {
  if (!whatsapp_instance || !whatsapp_instance.socket) return;
  whatsapp_instance.socket.off('signaling');
  whatsapp_instance.socket.on('signaling', async (...args) => {
    const data = args[0];
    store.dispatch('webphone/updateCallStatus', data?.tag);
    const tag = data?.tag;

    if (tag === 'offer') {
      const name = data?.content?.from_tag;
      const whatsapp_id = data?.content?.phone;
      const phone =
        parsePhoneNumber(`+${whatsapp_id}`)?.formatInternational() ??
        whatsapp_id;
      const profile_picture = data?.content?.profile_picture;
      store.dispatch('webphone/incomingCall', {
        token,
        phone,
        contact_name: name,
        profile_picture,
      });

      const wavoip_call_id =
        extractCallId(data) || `wvp-${token}-${whatsapp_id}-${Date.now()}`;
      const key = callKey(token, whatsapp_id);
      const logged = await logCall({
        token,
        phone: whatsapp_id,
        direction: 'inbound',
        status: 'ringing',
        wavoip_call_id,
        from_tag: name,
        profile_picture,
      });
      activeCalls.set(key, {
        wavoip_call_id,
        message_id: logged?.message_id,
        direction: 'inbound',
      });
    } else if (tag === 'accept') {
      const whatsapp_id = data?.content?.phone || callInfo.value.phone;
      const entry = activeCalls.get(callKey(token, whatsapp_id));
      if (entry) {
        await logCall({
          token,
          phone: whatsapp_id,
          direction: entry.direction,
          status: 'accept',
          wavoip_call_id: entry.wavoip_call_id,
        });
      }
    } else if (
      ['terminate', 'reject', 'accept_elsewhere', 'reject_elsewhere'].includes(
        tag
      )
    ) {
      const whatsapp_id = data?.content?.phone || callInfo.value.phone;
      const entry = activeCalls.get(callKey(token, whatsapp_id));
      if (entry) {
        const finalStatus =
          tag === 'terminate' && callInfo.value.active_start_date
            ? 'completed'
            : tag;
        const result = await logCall({
          token,
          phone: whatsapp_id,
          direction: entry.direction,
          status: finalStatus,
          wavoip_call_id: entry.wavoip_call_id,
          duration: elapsedTime.value,
        });
        const messageId = entry.message_id || result?.message_id;
        if (finalStatus === 'completed' && messageId) {
          setTimeout(() => fetchRecordingForMessage(messageId), 4000);
        }
        activeCalls.delete(callKey(token, whatsapp_id));
      }
      if (resetCallTimeout.value) clearTimeout(resetCallTimeout.value);
      resetCallTimeout.value = setTimeout(() => {
        store.dispatch('webphone/resetCall');
        resetCallTimeout.value = null;
      }, 3500);
    }
  });
}

function playCalling() {
  callingSound.value = new Audio(SoundCalling);
  callingSound.value.loop = true;
  callingSound.value.play();
}

function stopCalling() {
  if (callingSound.value) {
    callingSound.value.pause();
    callingSound.value.currentTime = 0;
  }
}

function playRinging() {
  ringSound.value = new Audio(SoundRinging);
  ringSound.value.loop = true;
  ringSound.value.play();
}

function stopRinging() {
  if (ringSound.value) {
    ringSound.value.pause();
    ringSound.value.currentTime = 0;
  }
}

watch(inboxes, newInboxes => {
  newInboxes.forEach(inbox => {
    if (inbox.external_tokens?.length || inbox.external_token) {
      const tokens = inbox.external_tokens?.length
        ? inbox.external_tokens
        : inbox.external_token
            .split(',')
            .map(token => token.trim())
            .filter(Boolean);
      tokens.forEach(token => startWavoip(inbox.name, token));
    }
  });
});

watch(callInfo, async (newCallInfo, oldCallInfo) => {
  const instances = store.state.webphone.wavoip;
  Object.keys(instances).forEach(token => {
    listenSockets(token, instances[token].whatsapp_instance);
  });
  const status = newCallInfo.status;
  if (status === 'accept') startTimer();
  else if (status === 'terminate') stopTimer();
  if (status === 'outcoming_calling') playCalling();
  else stopCalling();
  if (status === 'offer') playRinging();
  else stopRinging();

  // Log outbound calls — inbound goes through listenSockets
  if (
    status === 'outcoming_calling' &&
    oldCallInfo?.status !== 'outcoming_calling' &&
    newCallInfo.direction === 'outcoming' &&
    newCallInfo.phone
  ) {
    const token = newCallInfo.whatsapp_instance;
    const key = callKey(token, newCallInfo.phone);
    if (!activeCalls.has(key)) {
      const wavoip_call_id = `wvp-${token}-${String(newCallInfo.phone).replace(/\D/g, '')}-${Date.now()}`;
      const logged = await logCall({
        token,
        phone: newCallInfo.phone,
        direction: 'outbound',
        status: 'ringing',
        wavoip_call_id,
        from_tag: newCallInfo.tag,
        profile_picture: newCallInfo.picture_profile,
      });
      activeCalls.set(key, {
        wavoip_call_id,
        message_id: logged?.message_id,
        direction: 'outbound',
      });
    }
  }
});

watch(wavoip, newWavoip => {
  Object.keys(newWavoip).forEach(token => {
    listenSockets(token, newWavoip[token].whatsapp_instance);
  });
});

onMounted(() => {
  // intentionally left blank
});

onBeforeUnmount(() => {
  stopTimer();
  stopCalling();
  stopRinging();
  if (resetCallTimeout.value) {
    clearTimeout(resetCallTimeout.value);
    resetCallTimeout.value = null;
  }
});
</script>

<template>
  <Draggable :initial-x="50" :initial-y="50">
    <div
      v-if="uiFlags.isOpen && callInfo.id && callInfo.status !== 'offer'"
      class="flex flex-col h-[315px] w-[250px] bg-white dark:bg-slate-900 opacity-90 border border-gray-500 rounded-xl select-none cursor-grab"
    >
      <div class="flex h-[30px] place-content-between items-center">
        <p class="text-xs text-slate-800 dark:text-slate-100 m-0 px-4">
          {{ callInfo.inbox_name }}
        </p>

        <button
          class="relative m-0 px-4 select-none rounded-lg bg-gray-900 text-center align-middle font-sans text-xs font-medium uppercase text-white transition-all hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          data-ripple-light="true"
          @click="closeWebphone"
        >
          <span
            class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
          >
            <fluent-icon
              icon="dismiss"
              size="14px"
              class="text-slate-800 dark:text-slate-100"
            />
          </span>
        </button>
      </div>
      <div class="flex justify-center align-middle mt-2">
        <Avatar
          :src="callInfo.picture_profile"
          :name="username"
          status="online"
          should-show-status-always
          :size="50"
          rounded-full
        />
      </div>
      <div class="flex flex-col my-6">
        <p
          v-if="callInfo.tag"
          class="text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ callInfo.tag }}
        </p>
        <p v-else class="text-slate-800 dark:text-slate-100 m-0 text-center">
          {{ callInfo.phone }}
        </p>

        <p
          v-if="callInfo.status === 'accept'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ formattedDuration }}
        </p>
        <p
          v-if="callInfo.status === 'accept_elsewhere'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.ACCEPT_ELSEWHERE') }}
        </p>
        <p
          v-if="callInfo.status === 'reject_elsewhere'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.REJECT_ELSEWHERE') }}
        </p>
        <p
          v-if="callInfo.status === 'terminate'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.TERMINATE') }}
        </p>
        <p
          v-if="callInfo.status === 'reject'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.TERMINATE') }}
        </p>
        <p
          v-if="callInfo.status === 'outcoming_calling'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.CONNECT_CALLING') }}
        </p>
        <p
          v-if="callInfo.status === 'preaccept'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.CALLING') }}
        </p>
        <p
          v-if="callInfo.status === 'relaylatency'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.CALLING') }}
        </p>
      </div>
      <div class="flex justify-center align-center gap-x-5 my-2">
        <span
          v-if="!isMuted"
          v-tooltip="$t('WEBPHONE.MUTE')"
          class="cursor-pointer text-slate-800 dark:text-slate-100 hover:text-slate-700 dark:hover:text-slate-300"
          @click="mute"
        >
          <Microphone :size="20" color="currentColor" />
        </span>
        <span
          v-else
          v-tooltip="$t('WEBPHONE.UNMUTE')"
          class="cursor-pointer text-red-300 hover:text-red-500"
          @click="unMute"
        >
          <MicrophoneSlash :size="20" color="currentColor" />
        </span>
        <span
          v-tooltip="$t('WEBPHONE.TURN_ON_VIDEO')"
          class="cursor-no-drop text-slate-600 dark:text-slate-300"
          :disabled="false"
        >
          <VideoIcon :size="20" color="currentColor" />
        </span>
        <span
          v-tooltip="$t('WEBPHONE.NUMPAD')"
          class="cursor-no-drop text-slate-600 dark:text-slate-300"
        >
          <NumpadVue :size="20" color="currentColor" />
        </span>
        <span
          v-tooltip="$t('WEBPHONE.TRANSFER')"
          class="cursor-no-drop text-slate-600 dark:text-slate-300"
        >
          <PhoneTransfer :size="20" color="currentColor" />
        </span>
      </div>
      <div class="flex justify-center align-center my-4">
        <button
          class="relative h-15 max-h-[30px] w-15 max-w-[30px] p-[17px] select-none rounded-full bg-red-500 text-center align-middle font-sans text-xs font-medium uppercase text-white shadow-md shadow-gray-900/10 transition-all hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          data-ripple-light="true"
          :disabled="isCallEndDisabled"
          @click="endCall"
        >
          <span
            class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
          >
            <PhoneSlash :size="15" color="white" />
          </span>
        </button>
      </div>
    </div>
    <div
      v-if="uiFlags.isOpen && callInfo.id && callInfo.status === 'offer'"
      class="flex flex-col h-[315px] w-[250px] bg-white dark:bg-slate-900 opacity-90 border border-gray-500 rounded-xl select-none cursor-grab"
    >
      <div class="flex h-[30px] place-content-between items-center">
        <p class="text-xs text-slate-800 dark:text-slate-100 m-0 px-4">
          {{ callInfo.inbox_name }}
        </p>

        <button
          class="relative m-0 px-4 select-none rounded-lg bg-gray-900 text-center align-middle font-sans text-xs font-medium uppercase text-white transition-all hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          data-ripple-light="true"
          @click="closeWebphone"
        >
          <span
            class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
          >
            <fluent-icon
              icon="dismiss"
              size="14px"
              class="text-slate-800 dark:text-slate-100"
            />
          </span>
        </button>
      </div>
      <div class="flex justify-center align-middle mt-2">
        <Avatar
          :src="callInfo.picture_profile"
          :name="username"
          status="online"
          should-show-status-always
          :size="50"
          rounded-full
        />
      </div>
      <div class="flex flex-col my-6">
        <p
          v-if="callInfo.tag"
          class="text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ callInfo.tag }}
        </p>
        <p v-else class="text-slate-800 dark:text-slate-100 m-0 text-center">
          {{ $t('WEBPHONE.UNKNOWN') }}
        </p>

        <p
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ callInfo.phone }}
        </p>
        <p
          v-if="callInfo.status === 'terminate'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.TERMINATE') }}
        </p>
        <p
          v-if="callInfo.status === 'reject'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.TERMINATE') }}
        </p>
        <p
          v-if="callInfo.status === 'outcoming_calling'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.CONNECT_CALLING') }}
        </p>
        <p
          v-if="callInfo.status === 'preaccept'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.CALLING') }}
        </p>
        <p
          v-if="callInfo.status === 'relaylatency'"
          class="text-lg font-medium text-slate-800 dark:text-slate-100 m-0 text-center"
        >
          {{ $t('WEBPHONE.CALLING') }}
        </p>
      </div>
      <div class="flex justify-center align-center my-4 gap-x-4">
        <button
          class="relative h-15 max-h-[30px] w-15 max-w-[30px] p-[17px] select-none rounded-full bg-green-500 text-center align-middle font-sans text-xs font-medium uppercase text-white shadow-md shadow-gray-900/10 transition-all hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          data-ripple-light="true"
          :disabled="isCallEndDisabled"
          @click="acceptCall"
        >
          <span
            class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
          >
            <Phone :size="15" color="white" />
          </span>
        </button>
        <button
          class="relative h-15 max-h-[30px] w-15 max-w-[30px] p-[17px] select-none rounded-full bg-red-500 text-center align-middle font-sans text-xs font-medium uppercase text-white shadow-md shadow-gray-900/10 transition-all hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          data-ripple-light="true"
          :disabled="isCallEndDisabled"
          @click="rejectCall"
        >
          <span
            class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
          >
            <PhoneSlash :size="15" color="white" />
          </span>
        </button>
      </div>
    </div>
  </Draggable>
</template>
